import React, { useEffect, useMemo, useRef, useState } from "react";
import SEO from "./components/SEO";
import * as Tone from "tone";
import { analyze } from "web-audio-beat-detector";
import * as Pitchfinder from "pitchfinder";
import { FaDrum, FaGuitar, FaKeyboard, FaMusic, FaWaveSquare } from "react-icons/fa";
import YouTube from "react-youtube";
import { useLocation } from "react-router-dom";
import "./Notes.css";

const KEYS = ["C Major", "G Major", "D Minor", "A Minor", "F Major", "E Minor", "Bb Major", "E Major"];
const TEMPOS = [72, 84, 92, 104, 116, 128, 140];
const MOODS = ["uplifting", "reflective", "anthemic", "intimate", "joyful", "cinematic"];
const STRUCTURES = [
  "Intro / Verse / Chorus / Verse / Bridge / Chorus",
  "Intro / Verse / Pre-Chorus / Chorus / Verse / Chorus / Outro",
  "Intro / Verse / Chorus / Verse / Chorus / Tag",
  "Intro / Verse / Chorus / Bridge / Chorus",
];

const DRUM_GROOVES = [
  "Kick on 1 & 3, snare on 2 & 4, open hat on the and of 4",
  "Tight pocket with ghost notes, ride pattern on 8ths",
  "Half-time feel with tom fills before every chorus",
  "Four-on-the-floor with syncopated kick variation",
];

const DRUM_FILLS = [
  "Bar 4: snare flam into tom triplet run",
  "Bar 8: kick-stop then crash on the 1",
  "Bar 12: rolling tom build with crescendo",
  "Bar 16: snare drag into cymbal swell",
];

const PIANO_PROGRESSIONS = [
  "I - V - vi - IV",
  "vi - IV - I - V",
  "I - IV - ii - V",
  "I - V - IV - I",
];

const PIANO_VOICINGS = [
  "Wide triads with open fifths in the left hand",
  "Broken chords, right hand on 16ths",
  "Octave bass with light chord stabs",
  "Dense mid-range voicing, sustain pedal on",
];

const GUITAR_STRUMS = [
  "Down • Down • Up • Up • Down • Up",
  "Down • Down • Down • Up",
  "Down • (mute) • Down • Up • Down",
  "Down • Down • Up • (pause) • Up • Down",
];

const GUITAR_TEXTURES = [
  "Palm-mute the verse, open up in the chorus.",
  "Let chords ring, add light swells on the bridge.",
  "Use arpeggios in the verse, full strum in chorus.",
  "Anchor the rhythm with steady downstrokes.",
];

const VIOLIN_LINES = [
  "Long lyrical line on the top melody",
  "Countermelody that answers every vocal phrase",
  "Arpeggiated line climbing into the chorus",
  "Warm sustained harmony on the third",
];

const VIOLIN_ARTICULATION = [
  "Legato with gentle vibrato",
  "Marcato accents on chorus hits",
  "Spiccato rhythm over the verse",
  "Portamento slide into the final chorus",
];

const ARRANGEMENT_MAPS = [
  [
    "0:00 - 0:12: pad + soft percussion",
    "0:12 - 0:42: piano enters, drums brush",
    "0:42 - 1:05: full groove, strings lift",
    "1:05 - 1:24: drop to piano + violin",
    "1:24 - 1:48: full chorus, cymbal swells",
  ],
  [
    "0:00 - 0:18: piano motif",
    "0:18 - 0:50: drums establish pocket",
    "0:50 - 1:16: chorus open hats",
    "1:16 - 1:34: bridge with violin line",
    "1:34 - 1:58: chorus reprise + tag",
  ],
  [
    "0:00 - 0:15: ambient intro",
    "0:15 - 0:45: verse groove",
    "0:45 - 1:10: chorus hits",
    "1:10 - 1:36: breakdown with piano only",
    "1:36 - 2:02: final lift",
  ],
];

const DRUM_PATTERNS = [
  "Bars 1-4: Kick (1, &3), Snare (2,4), Closed hat 8ths",
  "Bars 5-8: Add ghost notes on snare, open hat on &4",
  "Bars 9-12: Half-time feel, tom fill on bar 12",
  "Bars 13-16: Build with crash + ride 8ths",
];

const PIANO_RIGHT_HAND = [
  "Bars 1-4: Arp (I - V - vi - IV), sustain pedal",
  "Bars 5-8: Octave melody on top, light syncopation",
  "Bars 9-12: Broken chords, accent 2 & 4",
  "Bars 13-16: Block chords, wider voicings",
];

const PIANO_LEFT_HAND = [
  "Bars 1-4: Root + fifth, quarter notes",
  "Bars 5-8: Octave bass with passing tone",
  "Bars 9-12: Half-note roots, hold through bar",
  "Bars 13-16: Driving 8ths, lock with kick",
];

const VIOLIN_PHRASES = [
  "Bars 1-4: Sustain harmony on 3rd above melody",
  "Bars 5-8: Counter line, stepwise motion",
  "Bars 9-12: Rise to 5th, vibrato on long tones",
  "Bars 13-16: Unison with melody, final swell",
];

const VOICING_VARIATIONS = [
  "Verse: keep density low, left hand pad only",
  "Chorus: open voicings, add 9ths on top",
  "Bridge: darker voicings, emphasize ii chord",
  "Outro: simplify, hold I chord",
];

const DYNAMICS_NOTES = [
  "Verse: mp, Chorus: f, Bridge: mf with swell",
  "Verse: p, Chorus: mf, Tag: f with cymbal wash",
  "Verse: mp, Pre-chorus: mf, Chorus: f with accents",
  "Verse: p, Chorus: mf, Outro: dim to pp",
];

const PAD_PATTERNS = {
  intro: (dense) => ({
    counts: ["1", "2", "3", "4"],
    rows: [
      { label: "Hi-Hat", hits: dense ? ["x", "x", "x", "x"] : ["x", "", "x", ""] },
      { label: "Kick", hits: dense ? ["o", "", "o", ""] : ["o", "", "", ""] },
      { label: "Snare", hits: ["-", "-", "-", "-"] },
    ],
    note: "Soft touch, feel the pulse.",
  }),
  verse: (dense) => ({
    counts: ["1", "&", "2", "&", "3", "&", "4", "&"],
    rows: [
      { label: "Hi-Hat", hits: dense ? ["x", "x", "x", "x", "x", "x", "x", "x"] : ["x", "", "x", "", "x", "", "x", ""] },
      { label: "Snare", hits: ["", "", "o", "", "", "", "o", ""] },
      { label: "Kick", hits: dense ? ["o", "", "o", "", "o", "", "", ""] : ["o", "", "", "", "o", "", "", ""] },
    ],
    note: "Keep it steady for the vocal.",
  }),
  prechorus: (dense) => ({
    counts: ["1", "&", "2", "&", "3", "&", "4", "&"],
    rows: [
      { label: "Hi-Hat", hits: ["x", "x", "x", "x", "x", "x", "x", "x"] },
      { label: "Snare", hits: ["", "", "o", "", "", "", "o", ""] },
      { label: "Kick", hits: dense ? ["o", "", "o", "", "o", "", "o", ""] : ["o", "", "o", "", "o", "", "", ""] },
    ],
    note: "Build a little louder.",
  }),
  chorus: (dense) => ({
    counts: ["1", "&", "2", "&", "3", "&", "4", "&"],
    rows: [
      { label: "Crash", hits: ["X", "", "", "", "", "", "", ""] },
      { label: "Hi-Hat", hits: ["x", "x", "x", "x", "x", "x", "x", "x"] },
      { label: "Snare", hits: ["", "", "O", "", "", "", "O", ""] },
      { label: "Kick", hits: dense ? ["O", "", "O", "", "O", "", "O", ""] : ["O", "", "", "O", "", "O", "", ""] },
    ],
    note: "Full worship lift.",
  }),
  bridge: (dense) => ({
    counts: ["1", "&", "2", "&", "3", "&", "4", "&"],
    rows: [
      { label: "Floor tom", hits: dense ? ["o", "", "o", "", "o", "", "o", ""] : ["o", "", "", "", "o", "", "", ""] },
      { label: "Kick", hits: dense ? ["o", "", "o", "", "o", "", "o", ""] : ["o", "", "", "", "o", "", "", ""] },
    ],
    note: "Swelling build into the next lift.",
  }),
  outro: () => ({
    counts: ["1", "2", "3", "4"],
    rows: [
      { label: "Crash", hits: ["X", "", "", ""] },
      { label: "Snare", hits: ["", "O", "", ""] },
      { label: "Kick", hits: ["O", "", "", ""] },
    ],
    note: "Let it ring.",
  }),
  fill: () => ({
    counts: ["1", "2", "3", "4"],
    rows: [
      { label: "Tom 1", hits: ["o", "", "", ""] },
      { label: "Tom 2", hits: ["", "o", "", ""] },
      { label: "Floor tom", hits: ["", "", "o", ""] },
      { label: "Crash", hits: ["", "", "", "X"] },
    ],
    note: "Easy transition fill.",
  }),
};

const labelToPadKey = (label = "") => {
  const value = label.toLowerCase();
  if (value.includes("intro")) return "intro";
  if (value.includes("verse")) return "verse";
  if (value.includes("pre")) return "prechorus";
  if (value.includes("chorus")) return "chorus";
  if (value.includes("bridge")) return "bridge";
  if (value.includes("outro") || value.includes("ending")) return "outro";
  return "verse";
};

const PD705_LAYOUT = `
┌─────────┬─────────┬─────────┐
│   C     │   R     │   O     │
│ Crash   │ Ride    │ Open HH │
├─────────┼─────────┼─────────┤
│  T1     │  T2     │   FT    │
│ HighTom │ MidTom  │ Floor   │
├─────────┼─────────┼─────────┤
│   B     │   S     │   H     │
│ Kick    │ Snare   │ Hi-Hat  │
└─────────┴─────────┴─────────┘
`.trim();

const PD705_SECTION_PLAN = [
  { key: "intro", title: "1️⃣ INTRO", bars: 2 },
  { key: "verse", title: "2️⃣ VERSE", bars: 4 },
  { key: "chorus", title: "3️⃣ CHORUS", bars: 4 },
  { key: "bridge", title: "4️⃣ BRIDGE", bars: 4 },
  { key: "outro", title: "5️⃣ OUTRO", bars: 2 },
];

const PD705_PATTERNS = {
  intro: [
    ["H", "H", "H", "H"],
    ["HB", "H", "HS", "H"],
  ],
  verse: [
    ["HB", "H", "HS", "H"],
  ],
  chorus: [
    ["C", "H", "HS", "H"],
    ["HB", "H", "HS", "H"],
  ],
  bridge: [
    ["HB", "H", "HS", "O"],
    ["HB", "H", "HS", "H"],
  ],
  outro: [
    ["C", "H", "HS", "H"],
    ["HB", "H", "–", "–"],
  ],
};

const labelToSectionKey = (label = "") => {
  const value = label.toLowerCase();
  if (value.includes("intro")) return "intro";
  if (value.includes("verse")) return "verse";
  if (value.includes("chorus")) return "chorus";
  if (value.includes("bridge")) return "bridge";
  if (value.includes("outro") || value.includes("ending")) return "outro";
  return "verse";
};

const barsFromDuration = (duration, tempo) => {
  if (!duration || !Number.isFinite(duration)) return 2;
  const beats = (duration / 60) * (tempo || 84);
  const bars = Math.round(beats / 4);
  return Math.min(8, Math.max(2, bars));
};

const buildSectionPlan = (sections, tempo) => {
  const countsByKey = {};
  if (sections?.length) {
    sections.forEach((section) => {
      const key = labelToSectionKey(section.label);
      countsByKey[key] = (countsByKey[key] || 0) + barsFromDuration(section.end - section.start, tempo);
    });
  }
  return PD705_SECTION_PLAN.map((section) => ({
    ...section,
    bars: countsByKey[section.key] || section.bars,
  }));
};

const sanitizeCrash = (pattern, allowCrash) =>
  pattern.map((hit) => (hit === "C" && !allowCrash ? "H" : hit));

const buildPd705Sections = ({ sections, tempo }) => {
  const plan = buildSectionPlan(sections, tempo);
  let barNumber = 1;
  return plan.map((section) => {
    const patterns = PD705_PATTERNS[section.key] || PD705_PATTERNS.verse;
    const lines = [];
    for (let i = 0; i < section.bars; i += 1) {
      const rawPattern = patterns[i % patterns.length];
      const pattern = sanitizeCrash(rawPattern, i === 0);
      lines.push(`Bar ${barNumber}:  ${pattern.join(" , ")}`);
      barNumber += 1;
    }
    return { title: section.title, lines };
  });
};

const buildPadSheetSections = (sections, tempo) => {
  const dense = tempo >= 95;
  if (!sections?.length) {
    return [
      { title: "Intro (very soft)", ...PAD_PATTERNS.intro(dense) },
      { title: "Verse (main groove)", ...PAD_PATTERNS.verse(dense) },
      { title: "Pre-chorus (small build)", ...PAD_PATTERNS.prechorus(dense) },
      { title: "Chorus (main worship)", ...PAD_PATTERNS.chorus(dense) },
      { title: "Fill (before next section)", ...PAD_PATTERNS.fill() },
      { title: "Bridge (soft to build)", ...PAD_PATTERNS.bridge(dense) },
      { title: "Final chorus (big but controlled)", ...PAD_PATTERNS.chorus(true) },
      { title: "Ending (stop & ring)", ...PAD_PATTERNS.outro() },
    ];
  }

  const built = sections.map((section) => {
    const key = labelToPadKey(section.label);
    const pattern = PAD_PATTERNS[key] ? PAD_PATTERNS[key](dense) : PAD_PATTERNS.verse(dense);
    return {
      title: `${section.label} (${formatTimestamp(section.start)} - ${formatTimestamp(section.end)})`,
      start: section.start,
      end: section.end,
      ...pattern,
    };
  });

  const withFill = [];
  built.forEach((section, index) => {
    withFill.push(section);
    if (index < built.length - 1 && (section.title.toLowerCase().includes("chorus") || section.title.toLowerCase().includes("bridge"))) {
      withFill.push({ title: "Fill (transition)", ...PAD_PATTERNS.fill() });
    }
  });

  return withFill;
};

const hashBytes = (bytes) => {
  let hash = 0;
  for (let i = 0; i < bytes.length; i += 1) {
    hash = (hash << 5) - hash + bytes[i];
    hash |= 0;
  }
  return Math.abs(hash);
};

const pick = (list, seed, offset = 0) => list[(seed + offset) % list.length];

const PITCH_CLASS_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
const FLAT_TO_SHARP = { Bb: "A#", Eb: "D#", Ab: "G#", Db: "C#", Gb: "F#", Cb: "B", Fb: "E" };
const MAJOR_INTERVALS = [0, 2, 4, 5, 7, 9, 11];
const MINOR_INTERVALS = [0, 2, 3, 5, 7, 8, 10];
const MAJOR_QUALITIES = ["", "m", "m", "", "", "m", "dim"];
const MINOR_QUALITIES = ["m", "dim", "", "m", "m", "", ""];
const PROGRESSION_DEGREES = [
  [1, 5, 6, 4],
  [6, 4, 1, 5],
  [1, 4, 2, 5],
  [1, 5, 4, 1],
];
const DEFAULT_SECTION_LABELS = ["Intro", "Verse", "Pre-Chorus", "Chorus", "Verse 2", "Bridge", "Final Chorus", "Outro"];
const MIDI_PPQ = 480;

const rotate = (list, shift) => list.map((_, index) => list[(index + shift) % list.length]);

const normalizeVector = (vector) => {
  const sum = vector.reduce((total, value) => total + value, 0) || 1;
  return vector.map((value) => value / sum);
};

const correlation = (left, right) => {
  const meanLeft = left.reduce((total, value) => total + value, 0) / left.length;
  const meanRight = right.reduce((total, value) => total + value, 0) / right.length;
  let numerator = 0;
  let leftDenom = 0;
  let rightDenom = 0;
  for (let i = 0; i < left.length; i += 1) {
    const leftDiff = left[i] - meanLeft;
    const rightDiff = right[i] - meanRight;
    numerator += leftDiff * rightDiff;
    leftDenom += leftDiff * leftDiff;
    rightDenom += rightDiff * rightDiff;
  }
  const denom = Math.sqrt(leftDenom * rightDenom) || 1;
  return numerator / denom;
};

const detectKeyFromHistogram = (histogram) => {
  const normalized = normalizeVector(histogram);
  let bestScore = -Infinity;
  let bestKey = "C Major";
  for (let i = 0; i < 12; i += 1) {
    const majorScore = correlation(normalized, normalizeVector(rotate(MAJOR_PROFILE, i)));
    const minorScore = correlation(normalized, normalizeVector(rotate(MINOR_PROFILE, i)));
    if (majorScore > bestScore) {
      bestScore = majorScore;
      bestKey = `${PITCH_CLASS_NAMES[i]} Major`;
    }
    if (minorScore > bestScore) {
      bestScore = minorScore;
      bestKey = `${PITCH_CLASS_NAMES[i]} Minor`;
    }
  }
  return bestKey;
};

const parseKeySignature = (key = "") => {
  const [rootRaw, modeRaw] = key.split(" ");
  if (!rootRaw) {
    return { root: "C", mode: "Major" };
  }
  const normalizedRoot = FLAT_TO_SHARP[rootRaw] || rootRaw;
  const mode = modeRaw && modeRaw.toLowerCase().includes("minor") ? "Minor" : "Major";
  return { root: normalizedRoot, mode };
};

const buildScaleChords = (key) => {
  const { root, mode } = parseKeySignature(key);
  const rootIndex = PITCH_CLASS_NAMES.indexOf(root);
  if (rootIndex === -1) {
    return ["C", "Dm", "Em", "F", "G", "Am", "Bdim"];
  }
  const intervals = mode === "Minor" ? MINOR_INTERVALS : MAJOR_INTERVALS;
  const qualities = mode === "Minor" ? MINOR_QUALITIES : MAJOR_QUALITIES;
  return intervals.map((interval, index) => {
    const note = PITCH_CLASS_NAMES[(rootIndex + interval) % 12];
    return `${note}${qualities[index]}`;
  });
};

const progressionToChords = (degrees, chordSet) =>
  degrees.map((degree) => chordSet[(degree - 1) % chordSet.length]);

const parseChord = (chord) => {
  const match = String(chord || "").match(/^([A-G](?:#|b)?)(dim|m)?/i);
  if (!match) return null;
  const root = FLAT_TO_SHARP[match[1]] || match[1];
  const quality = match[2] || "";
  return { root, quality };
};

const chordToMidiNotes = (chord, baseNote) => {
  const parsed = parseChord(chord);
  if (!parsed) return [];
  const rootIndex = PITCH_CLASS_NAMES.indexOf(parsed.root);
  if (rootIndex === -1) return [];
  const base = baseNote + rootIndex;
  if (parsed.quality === "dim") {
    return [base, base + 3, base + 6];
  }
  if (parsed.quality === "m") {
    return [base, base + 3, base + 7];
  }
  return [base, base + 4, base + 7];
};

const encodeVarInt = (value) => {
  let buffer = value & 0x7f;
  const bytes = [];
  while ((value >>= 7)) {
    buffer <<= 8;
    buffer |= (value & 0x7f) | 0x80;
  }
  while (true) {
    bytes.push(buffer & 0xff);
    if (buffer & 0x80) {
      buffer >>= 8;
    } else {
      break;
    }
  }
  return bytes;
};

const pushEvent = (events, delta, data) => {
  events.push(...encodeVarInt(delta), ...data);
};

const buildMidiFile = (tempo, events) => {
  const header = [
    0x4d, 0x54, 0x68, 0x64,
    0x00, 0x00, 0x00, 0x06,
    0x00, 0x00,
    0x00, 0x01,
    (MIDI_PPQ >> 8) & 0xff,
    MIDI_PPQ & 0xff,
  ];

  const trackHeader = [0x4d, 0x54, 0x72, 0x6b];
  const trackLength = events.length;
  const trackLengthBytes = [
    (trackLength >> 24) & 0xff,
    (trackLength >> 16) & 0xff,
    (trackLength >> 8) & 0xff,
    trackLength & 0xff,
  ];
  return new Uint8Array([...header, ...trackHeader, ...trackLengthBytes, ...events]);
};

const buildMidiEvents = (tempo, eventBuilder) => {
  const events = [];
  const tempoValue = Math.round(60000000 / (tempo || 90));
  pushEvent(events, 0, [0xff, 0x51, 0x03, (tempoValue >> 16) & 0xff, (tempoValue >> 8) & 0xff, tempoValue & 0xff]);
  pushEvent(events, 0, [0xff, 0x58, 0x04, 0x04, 0x02, 0x18, 0x08]);
  eventBuilder(events);
  pushEvent(events, 0, [0xff, 0x2f, 0x00]);
  return events;
};

const buildChordMidi = (tempo, sectionNotes, baseNote) => {
  const events = buildMidiEvents(tempo, (ev) => {
    const channel = 0;
    sectionNotes.forEach((section) => {
      const chords = section.chords.split(" - ").filter(Boolean);
      const durationSeconds = section.end && section.start ? section.end - section.start : 8;
      const beats = Math.max(4, Math.round((durationSeconds / 60) * tempo));
      const chordBeats = Math.max(1, Math.floor(beats / chords.length));
      chords.forEach((chord) => {
        const notes = chordToMidiNotes(chord, baseNote);
        const durationTicks = chordBeats * MIDI_PPQ;
        notes.forEach((note, index) =>
          pushEvent(ev, index === 0 ? 0 : 0, [0x90 + channel, note, 90])
        );
        notes.forEach((note, index) =>
          pushEvent(ev, index === 0 ? durationTicks : 0, [0x80 + channel, note, 64])
        );
      });
    });
  });
  return buildMidiFile(tempo, events);
};

const buildDrumMidi = (tempo, sectionNotes) => {
  const events = buildMidiEvents(tempo, (ev) => {
    const channel = 9;
    const kick = 36;
    const snare = 38;
    const hihat = 42;
    sectionNotes.forEach((section) => {
      const durationSeconds = section.end && section.start ? section.end - section.start : 8;
      const bars = Math.max(1, Math.round(durationSeconds / (60 / tempo * 4)));
      for (let bar = 0; bar < bars; bar += 1) {
        for (let beat = 0; beat < 4; beat += 1) {
          const delta = beat === 0 && bar === 0 ? 0 : MIDI_PPQ;
          pushEvent(ev, delta, [0x99, hihat, 70]);
          if (beat === 0 || beat === 2) {
            pushEvent(ev, 0, [0x99, kick, 90]);
          }
          if (beat === 1 || beat === 3) {
            pushEvent(ev, 0, [0x99, snare, 90]);
          }
        }
      }
    });
  });
  return buildMidiFile(tempo, events);
};

const downloadMidi = (filename, midiBytes) => {
  const blob = new Blob([midiBytes], { type: "audio/midi" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const analyzeKey = (audioBuffer) => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const detectPitch = Pitchfinder.YIN({ sampleRate });
  const frameSize = 2048;
  const hopSize = 1024;
  const pitchHistogram = new Array(12).fill(0);
  const maxFrames = 1200;
  let framesProcessed = 0;

  for (let i = 0; i + frameSize < channelData.length; i += hopSize * 2) {
    const frame = channelData.subarray(i, i + frameSize);
    const pitch = detectPitch(frame);
    if (pitch && pitch > 0) {
      const midi = 69 + 12 * Math.log2(pitch / 440);
      const pitchClass = ((Math.round(midi) % 12) + 12) % 12;
      let energy = 0;
      for (let j = 0; j < frame.length; j += 1) {
        energy += Math.abs(frame[j]);
      }
      pitchHistogram[pitchClass] += energy / frame.length;
    }
    framesProcessed += 1;
    if (framesProcessed >= maxFrames) break;
  }

  if (pitchHistogram.every((value) => value === 0)) {
    return "Unknown";
  }

  return detectKeyFromHistogram(pitchHistogram);
};

const formatTimestamp = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const tempoLabelFromBpm = (bpm) => {
  if (!bpm) return "Unknown tempo";
  if (bpm < 70) return "Slow worship";
  if (bpm < 95) return "Mid-tempo worship";
  if (bpm < 120) return "Upbeat worship";
  return "Fast worship";
};

const detectSections = (audioBuffer, tempo) => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const windowSize = Math.max(1, Math.floor(sampleRate * 0.5));
  const energies = [];

  for (let i = 0; i < channelData.length; i += windowSize) {
    const end = Math.min(i + windowSize, channelData.length);
    let sum = 0;
    for (let j = i; j < end; j += 1) {
      sum += channelData[j] * channelData[j];
    }
    const rms = Math.sqrt(sum / (end - i || 1));
    energies.push({ time: i / sampleRate, rms });
  }

  if (energies.length < 4) {
    return [{ label: "Full Song", start: 0, end: audioBuffer.duration }];
  }

  const deltas = energies.slice(1).map((entry, index) => ({
    index: index + 1,
    delta: Math.abs(entry.rms - energies[index].rms),
  }));
  const avgDelta = deltas.reduce((total, entry) => total + entry.delta, 0) / deltas.length || 0.0001;
  const threshold = avgDelta * 1.6;
  const minGapSeconds = Math.max(8, (60 / (tempo || 80)) * 4);
  const minGapWindows = Math.ceil(minGapSeconds / 0.5);

  const candidates = deltas
    .filter((entry) => entry.delta >= threshold)
    .sort((a, b) => b.delta - a.delta);

  const boundaries = [];
  candidates.forEach((entry) => {
    if (boundaries.length >= 5) return;
    const tooClose = boundaries.some((boundary) => Math.abs(boundary - entry.index) < minGapWindows);
    if (!tooClose) {
      boundaries.push(entry.index);
    }
  });

  boundaries.sort((a, b) => a - b);

  const segmentIndices = [0, ...boundaries, energies.length - 1];
  const labels = ["Intro", "Verse", "Pre-Chorus", "Chorus", "Bridge", "Chorus", "Outro"];

  return segmentIndices.slice(0, -1).map((startIndex, idx) => {
    const endIndex = segmentIndices[idx + 1];
    const startTime = energies[startIndex].time;
    const endTime = idx === segmentIndices.length - 2 ? audioBuffer.duration : energies[endIndex].time;
    return {
      label: labels[idx] || `Section ${idx + 1}`,
      start: startTime,
      end: endTime,
    };
  });
};

const buildTimedSections = (duration) => {
  if (!duration || !Number.isFinite(duration)) return [];
  const segmentLength = duration / DEFAULT_SECTION_LABELS.length;
  return DEFAULT_SECTION_LABELS.map((label, index) => ({
    label,
    start: index * segmentLength,
    end: index === DEFAULT_SECTION_LABELS.length - 1 ? duration : (index + 1) * segmentLength,
  }));
};

const buildNotesFromSeed = (seed, analysis = {}) => {
  const tempo = analysis.tempo || pick(TEMPOS, seed);
  const key = analysis.key || pick(KEYS, seed, 2);
  const mood = pick(MOODS, seed, 5);
  const sections = analysis.sections || [];
  const structure = sections.length ? sections.map((section) => section.label).join(" / ") : pick(STRUCTURES, seed, 1);
  const arrangement =
    sections.length
      ? sections.map((section) => `${section.label}: ${formatTimestamp(section.start)} - ${formatTimestamp(section.end)}`)
      : pick(ARRANGEMENT_MAPS, seed, 3);
  const chordSet = buildScaleChords(key);
  const sectionNotes = (sections.length ? sections : DEFAULT_SECTION_LABELS.map((label) => ({ label }))).map(
    (section, index) => {
      const progression = progressionToChords(PROGRESSION_DEGREES[(seed + index) % PROGRESSION_DEGREES.length], chordSet);
      const groove = pick(DRUM_GROOVES, seed, index);
      const fill = pick(DRUM_FILLS, seed, index + 2);
      return {
        label: section.label,
        start: section.start,
        end: section.end,
        chords: progression.join(" - "),
        drums: {
          groove,
          fill,
        },
        piano: {
          progression: progression.join(" - "),
          voicing: pick(PIANO_VOICINGS, seed, index + 1),
        },
        violin: {
          line: pick(VIOLIN_LINES, seed, index + 2),
          articulation: pick(VIOLIN_ARTICULATION, seed, index + 1),
        },
        guitar: {
          chords: progression.join(" - "),
          strum: pick(GUITAR_STRUMS, seed, index + 3),
          texture: pick(GUITAR_TEXTURES, seed, index + 4),
        },
      };
    }
  );

  return {
    tempo,
    key,
    mood,
    structure,
    arrangement,
    dynamics: pick(DYNAMICS_NOTES, seed, 6),
    drums: {
      groove: pick(DRUM_GROOVES, seed),
      fill: pick(DRUM_FILLS, seed, 4),
      dynamics: "Accent the downbeats, soften the verse hi-hats.",
      patterns: DRUM_PATTERNS.map((line, index) => pick(DRUM_PATTERNS, seed, index)),
    },
    piano: {
      progression: pick(PIANO_PROGRESSIONS, seed),
      voicing: pick(PIANO_VOICINGS, seed, 3),
      rhythm: "Keep the left hand steady; syncopate the right on the chorus.",
      rightHand: PIANO_RIGHT_HAND.map((line, index) => pick(PIANO_RIGHT_HAND, seed, index)),
      leftHand: PIANO_LEFT_HAND.map((line, index) => pick(PIANO_LEFT_HAND, seed, index)),
      variations: VOICING_VARIATIONS.map((line, index) => pick(VOICING_VARIATIONS, seed, index)),
    },
    violin: {
      line: pick(VIOLIN_LINES, seed),
      articulation: pick(VIOLIN_ARTICULATION, seed, 2),
      range: "Stay between G3 and E5 for warmth.",
      phrases: VIOLIN_PHRASES.map((line, index) => pick(VIOLIN_PHRASES, seed, index)),
    },
    padSheet: {
      timeSignature: "4/4",
      tempoLabel: tempoLabelFromBpm(tempo),
      sections: buildPadSheetSections(sections, tempo),
    },
    sections,
    sectionNotes,
  };
};

export default function Notes() {
  const location = useLocation();
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [notes, setNotes] = useState(null);
  const [processingStep, setProcessingStep] = useState(0);
  const [activeTab, setActiveTab] = useState("drums");
  const [activeTopTab, setActiveTopTab] = useState("source");
  const [audioUrl, setAudioUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [youtubePlayer, setYoutubePlayer] = useState(null);
  const [youtubePlaying, setYoutubePlaying] = useState(false);
  const [youtubeSectionsApplied, setYoutubeSectionsApplied] = useState(false);
  const [activeNotePreview, setActiveNotePreview] = useState("");
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewRef = useRef({ synth: null, part: null });
  const betaParam = useMemo(
    () => new URLSearchParams(location.search).get("beta") || "",
    [location.search]
  );
  const betaEnabled = ["on", "true", "1"].includes(betaParam.toLowerCase());
  const sourceLabel = audioFile?.name || youtubeUrl.trim() || "Untitled";
  const youtubeId = (() => {
    if (!youtubeUrl.trim()) return "";
    try {
      const url = new URL(youtubeUrl);
      if (url.hostname.includes("youtu.be")) {
        return url.pathname.replace("/", "");
      }
      return url.searchParams.get("v") || "";
    } catch {
      return "";
    }
  })();
  const notationSections = useMemo(() => {
    if (!notes) return [];
    return buildPd705Sections({
      sections: notes.sections,
      tempo: notes.tempo,
    });
  }, [notes]);

  const processSteps = [
    "Decoding audio...",
    "Detecting tempo...",
    "Detecting key center...",
    "Segmenting song sections...",
    "Building instrument notes...",
    "Finalizing arrangement...",
  ];

  useEffect(() => {
    if (!audioFile) {
      setAudioUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(audioFile);
    setAudioUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [audioFile]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!betaEnabled && activeTopTab === "notes") {
      setActiveTopTab("source");
    }
  }, [activeTopTab, betaEnabled]);

  useEffect(() => {
    if (!youtubePlayer || !youtubePlaying) return undefined;
    const timer = window.setInterval(() => {
      const time = youtubePlayer.getCurrentTime?.();
      if (Number.isFinite(time)) {
        setCurrentTime(time);
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [youtubePlayer, youtubePlaying]);

  const stopNotePreview = () => {
    if (previewRef.current.part) {
      previewRef.current.part.stop();
      previewRef.current.part.dispose();
      previewRef.current.part = null;
    }
    const synth = previewRef.current.synth;
    if (synth) {
      if (typeof synth.dispose === "function") {
        synth.dispose();
      } else {
        Object.values(synth).forEach((node) => {
          if (node && typeof node.dispose === "function") {
            node.dispose();
          }
        });
      }
      previewRef.current.synth = null;
    }
    Tone.Transport.stop();
    Tone.Transport.cancel();
    setActiveNotePreview("");
  };

  const buildChordEvents = (sectionNotes, baseMidi) => {
    const events = [];
    let barIndex = 0;
    sectionNotes.forEach((section) => {
      const chords = section.chords.split(" - ").filter(Boolean);
      chords.forEach((chord) => {
        const midiNotes = chordToMidiNotes(chord, baseMidi);
        const noteNames = midiNotes.map((midi) => Tone.Frequency(midi, "midi").toNote());
        events.push({
          time: `${barIndex}:0:0`,
          notes: noteNames,
          duration: "1m",
        });
        barIndex += 1;
      });
    });
    return events;
  };

  const playNotePreview = async (instrument) => {
    if (!notes) return;
    if (activeNotePreview === instrument) {
      stopNotePreview();
      return;
    }
    await Tone.start();
    stopNotePreview();
    Tone.Transport.bpm.value = notes.tempo || 90;

    if (instrument === "drums" || instrument === "pad") {
      const kick = new Tone.MembraneSynth({ volume: -6 }).toDestination();
      const snare = new Tone.NoiseSynth({ volume: -12 }).toDestination();
      const hat = new Tone.MetalSynth({ volume: -16, decay: 0.05 }).toDestination();
      previewRef.current.synth = { kick, snare, hat };
      const pattern = ["kick", "hat", "snare", "hat", "kick", "hat", "snare", "hat"];
      previewRef.current.part = new Tone.Sequence(
        (time, step) => {
          if (step === "kick") kick.triggerAttackRelease("C2", "8n", time);
          if (step === "snare") snare.triggerAttackRelease("8n", time);
          if (step === "hat") hat.triggerAttackRelease("16n", time);
        },
        pattern,
        "8n"
      ).start(0);
      Tone.Transport.start();
      setActiveNotePreview(instrument);
      return;
    }

    const synth =
      instrument === "violin"
        ? new Tone.Synth({ oscillator: { type: "sine" }, envelope: { attack: 0.05, release: 0.4 } }).toDestination()
        : new Tone.PolySynth(Tone.Synth, { envelope: { attack: 0.02, release: 0.6 } }).toDestination();
    previewRef.current.synth = synth;

    const baseNoteMap = {
      piano: 60,
      guitar: 52,
      violin: 72,
    };
    const events = buildChordEvents(notes.sectionNotes, baseNoteMap[instrument] || 60);
    previewRef.current.part = new Tone.Part((time, value) => {
      synth.triggerAttackRelease(value.notes, value.duration, time);
    }, events).start(0);
    Tone.Transport.start();
    setActiveNotePreview(instrument);
  };

  useEffect(() => {
    if (!youtubePlayer || !notes || youtubeSectionsApplied) return;
    const duration = youtubePlayer.getDuration?.();
    if (!Number.isFinite(duration) || duration <= 0) return;
    const sections = buildTimedSections(duration);
    if (!sections.length) return;
    setNotes((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections,
        structure: sections.map((section) => section.label).join(" / "),
        arrangement: sections.map(
          (section) => `${section.label}: ${formatTimestamp(section.start)} - ${formatTimestamp(section.end)}`
        ),
        padSheet: {
          ...prev.padSheet,
          sections: buildPadSheetSections(sections, prev.tempo),
        },
        sectionNotes: sections.map((section, index) => ({
          ...(prev.sectionNotes[index] || {}),
          label: section.label,
          start: section.start,
          end: section.end,
        })),
      };
    });
    setYoutubeSectionsApplied(true);
  }, [notes, youtubePlayer, youtubeSectionsApplied]);

  const activeSectionIndex = (() => {
    if (!notes?.sections?.length) return -1;
    const index = notes.sections.findIndex(
      (section) => typeof section.start === "number" && typeof section.end === "number" && currentTime >= section.start && currentTime < section.end
    );
    if (index !== -1) return index;
    if (currentTime >= notes.sections[notes.sections.length - 1].end) {
      return notes.sections.length - 1;
    }
    return -1;
  })();

  const activeSection = activeSectionIndex >= 0 ? notes?.sections?.[activeSectionIndex] : null;

  const handleGenerate = async (event) => {
    event.preventDefault();
    const source = audioFile?.name || youtubeUrl.trim() || "";

    if (!source) {
      setStatus("error");
      setStatusMessage("Add a YouTube link or upload audio to generate notes.");
      setNotes(null);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setStatus("processing");
    setProcessingStep(0);
    setStatusMessage(processSteps[0]);
    setNotes(null);

    try {
      if (!audioFile) {
        const seedValue = hashBytes(new TextEncoder().encode(source));
        const generated = buildNotesFromSeed(seedValue, {});
        setNotes(generated);
        setStatus("ready");
        setStatusMessage("Notes ready (upload audio for true analysis).");
        return;
      }

      const arrayBuffer = await audioFile.arrayBuffer();
      const sample = arrayBuffer.slice(0, 1024 * 1024);
      const seedValue = hashBytes(new Uint8Array(sample));

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
      await audioContext.close();

      setProcessingStep(1);
      setStatusMessage(processSteps[1]);
      const tempoRaw = await analyze(audioBuffer, { minTempo: 50, maxTempo: 180 });
      const tempo = Number.isFinite(tempoRaw) ? Math.round(tempoRaw) : pick(TEMPOS, seedValue);

      setProcessingStep(2);
      setStatusMessage(processSteps[2]);
      const key = analyzeKey(audioBuffer);

      setProcessingStep(3);
      setStatusMessage(processSteps[3]);
      const sections = detectSections(audioBuffer, tempo);

      setProcessingStep(4);
      setStatusMessage(processSteps[4]);
      const generated = buildNotesFromSeed(seedValue, { tempo, key, sections });

      setProcessingStep(5);
      setStatusMessage(processSteps[5]);
      timerRef.current = setTimeout(() => {
        setNotes(generated);
        setStatus("ready");
        setStatusMessage("Notes ready. Refine them for your band.");
      }, 500);
    } catch (error) {
      setStatus("error");
      setStatusMessage("Could not analyze the audio file. Please try another file.");
      setNotes(null);
    }
  };

  const handleReset = () => {
    setYoutubeUrl("");
    setAudioFile(null);
    setNotes(null);
    setStatus("idle");
    setStatusMessage("");
    setProcessingStep(0);
    setActiveTab("drums");
    setCurrentTime(0);
    setYoutubeSectionsApplied(false);
    stopNotePreview();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <main className="notes-page">
      <SEO
        title="Notes"
        description="Capture worship ideas and musical notes."
      />
      <section className="notes-top-tabs">
        <div className="notes-collapsible">
          <div className="notes-top-tabs-shell">
            <div className="notes-tabs" role="tablist" aria-label="Setup tabs">
              <button
                type="button"
                role="tab"
                aria-selected={activeTopTab === "source"}
                className={`notes-tab ${activeTopTab === "source" ? "active" : ""}`}
                onClick={() => setActiveTopTab("source")}
              >
                Source your song
              </button>
              {betaEnabled && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTopTab === "notes"}
                  className={`notes-tab ${activeTopTab === "notes" ? "active" : ""}`}
                  onClick={() => setActiveTopTab("notes")}
                >
                  Notes
                </button>
              )}
            </div>

            <div className={`notes-tab-panel ${activeTopTab === "source" ? "active" : ""}`} role="tabpanel">
              <form className="notes-panel notes-panel--compact notes-panel--flat" onSubmit={handleGenerate}>
                <div className="notes-input-row">
                  <label className="notes-input-group" htmlFor="notes-youtube">
                    <span>YouTube URL</span>
                    <input
                      id="notes-youtube"
                      type="url"
                      placeholder="https://www.youtube.com/watch?v=..."
                      value={youtubeUrl}
                      onChange={(event) => setYoutubeUrl(event.target.value)}
                    />
                    <span className="notes-hint">
                      For true analysis, upload the audio file (YouTube requires a server connector).
                    </span>
                  </label>

                  <label className="notes-input-group" htmlFor="notes-file">
                    <span>Upload audio (mp3, wav, m4a)</span>
                    <input
                      id="notes-file"
                      ref={fileInputRef}
                      type="file"
                      accept="audio/*"
                      onChange={(event) => setAudioFile(event.target.files?.[0] || null)}
                    />
                    {audioFile && <span className="notes-file">Selected: {audioFile.name}</span>}
                  </label>
                </div>

                <div className="notes-actions notes-actions--tight">
                  <button className="notes-primary" type="submit" disabled={status === "processing"}>
                    {status === "processing" ? "Generating..." : "Generate Notes"}
                  </button>
                  <button className="notes-secondary" type="button" onClick={handleReset}>
                    Reset
                  </button>
                </div>

                {statusMessage && (
                  <p className={`notes-status ${status}`} aria-live="polite">
                    {statusMessage}
                  </p>
                )}

                {status === "processing" && (
                  <div className="notes-progress" aria-live="polite">
                    {processSteps.map((step, index) => (
                      <div key={step} className={`notes-progress-step ${index <= processingStep ? "active" : ""}`}>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                )}
              </form>

              <div className="notes-faq-body notes-panel notes-panel--compact notes-panel--flat">
                <p>
                  Music Notes Builder generates drum, piano, violin, and guitar notes from your song. Upload audio for
                  true tempo/key detection, then follow the section highlights while the song plays.
                </p>
                <p>Notes include tempo, key, arrangement, and instrument cues you can rehearse right away.</p>
              </div>
            </div>

            {betaEnabled && (
              <div className={`notes-tab-panel ${activeTopTab === "notes" ? "active" : ""}`} role="tabpanel">
              <div className="notes-panel notes-panel--compact notes-panel--flat">
                {notes ? (
                  <div className="notes-summary">
                    <div className="notes-summary-grid">
                      <div>
                        <span className="notes-label">Key</span>
                        <p>{notes.key}</p>
                      </div>
                      <div>
                        <span className="notes-label">Tempo</span>
                        <p>{notes.tempo} BPM</p>
                      </div>
                      <div>
                        <span className="notes-label">Mood</span>
                        <p>{notes.mood}</p>
                      </div>
                      {sourceLabel && (
                        <div>
                          <span className="notes-label">Source</span>
                          <p className="notes-source">{sourceLabel}</p>
                        </div>
                      )}
                      <div>
                        <span className="notes-label">Structure</span>
                        <p>{notes.structure}</p>
                      </div>
                      <div>
                        <span className="notes-label">Dynamics</span>
                        <p>{notes.dynamics}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="notes-player-empty">Generate notes to see song overview.</p>
                )}

                <div className="notes-player-card notes-player-card--flat">
                  {activeSection && (
                    <div className="notes-now-playing">
                      Now: {activeSection.label} ({formatTimestamp(activeSection.start)} - {formatTimestamp(activeSection.end)})
                    </div>
                  )}
                  {audioUrl && (
                    <audio
                      controls
                      src={audioUrl}
                      onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
                      className="notes-audio"
                    />
                  )}
                  {youtubeId && (
                    <div className="notes-youtube">
                      <YouTube
                        videoId={youtubeId}
                        opts={{ width: "100%", playerVars: { modestbranding: 1 } }}
                        onReady={(event) => setYoutubePlayer(event.target)}
                        onStateChange={(event) => {
                          const state = event.data;
                          setYoutubePlaying(state === 1);
                        }}
                      />
                    </div>
                  )}
                  {!audioUrl && !youtubeId && (
                    <p className="notes-player-empty">Upload audio or paste a YouTube link to enable playback.</p>
                  )}
                </div>
              </div>
              {notes ? (
                <details className="notes-collapsible" open>
                  <summary>Notes</summary>
                  <div className="notes-tab-shell">
                    <div className="notes-tabs" role="tablist" aria-label="Notes tabs">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "drums"}
                        className={`notes-tab ${activeTab === "drums" ? "active" : ""}`}
                        onClick={() => setActiveTab("drums")}
                      >
                        Drum notes
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "piano"}
                        className={`notes-tab ${activeTab === "piano" ? "active" : ""}`}
                        onClick={() => setActiveTab("piano")}
                      >
                        Piano notes
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "violin"}
                        className={`notes-tab ${activeTab === "violin" ? "active" : ""}`}
                        onClick={() => setActiveTab("violin")}
                      >
                        Violin notes
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "guitar"}
                        className={`notes-tab ${activeTab === "guitar" ? "active" : ""}`}
                        onClick={() => setActiveTab("guitar")}
                      >
                        Guitar notes
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "pad"}
                        className={`notes-tab ${activeTab === "pad" ? "active" : ""}`}
                        onClick={() => setActiveTab("pad")}
                      >
                        9-pad drum sheet
                      </button>
                    </div>

                    <div className={`notes-tab-panel ${activeTab === "drums" ? "active" : ""}`} role="tabpanel">
                      <article className="notes-card notes-card--expanded">
                        <header>
                          <FaDrum aria-hidden="true" />
                          <h3>Drum notes</h3>
                          <button
                            type="button"
                            className="notes-export"
                            onClick={() => downloadMidi("drum-notes.mid", buildDrumMidi(notes.tempo, notes.sectionNotes))}
                          >
                            Export MIDI
                          </button>
                        </header>
                        <div className="notes-stem-controls">
                          <button type="button" className="notes-play" onClick={() => playNotePreview("drums")}>
                            {activeNotePreview === "drums" ? "Stop notes" : "Play notes"}
                          </button>
                        </div>
                        {notes.sections.length > 0 && (
                          <div className="notes-section-list">
                            {notes.sections.map((section, index) => (
                              <div
                                key={`${section.label}-${section.start}`}
                                className={`notes-section-item ${activeSectionIndex === index ? "active" : ""}`}
                              >
                                <span>{section.label}</span>
                                <span>
                                  {formatTimestamp(section.start)} - {formatTimestamp(section.end)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="notes-detail-grid">
                          <div>
                            <h4>Groove</h4>
                            <p>{notes.drums.groove}</p>
                          </div>
                          <div>
                            <h4>Fill cues</h4>
                            <p>{notes.drums.fill}</p>
                          </div>
                          <div>
                            <h4>Dynamics</h4>
                            <p>{notes.drums.dynamics}</p>
                          </div>
                        </div>
                        <div className="notes-detail-block">
                          <h4>Bar-by-bar pattern</h4>
                          <ul>
                            {notes.drums.patterns.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="notes-detail-block">
                          <h4>PD705 pad layout (player view)</h4>
                          <pre className="notes-layout-grid">{PD705_LAYOUT}</pre>
                        </div>
                        <div className="notes-detail-block">
                          <h4>PD705 worship drum notes</h4>
                          <div className="notes-pd705-grid" aria-label="PD705 drum notes">
                            {notationSections.map((section) => (
                              <div key={section.title} className="notes-pd705-tile">
                                <h5>{section.title}</h5>
                                <pre className="notes-code">{section.lines.join("\n")}</pre>
                              </div>
                            ))}
                          </div>
                          <div className="notes-guidance">
                            <h4>Beginner practice flow</h4>
                            <p>H -> HB -> HS -> full groove (slow, steady, and relaxed).</p>
                            <h4>Count it out</h4>
                            <p>Count 1-2-3-4 out loud. Each bar has four beats.</p>
                            <h4>Dynamics</h4>
                            <p>Soft verse, lifted chorus, gentle bridge. Keep it worshipful.</p>
                          </div>
                        </div>
                        <div className="notes-section-cards">
                          {notes.sectionNotes.map((section, index) => (
                            <div
                              key={`${section.label}-${index}`}
                              className={`notes-section-card ${activeSectionIndex === index ? "active" : ""}`}
                            >
                              <h4>{section.label}</h4>
                              <p>Groove: {section.drums.groove}</p>
                              <p>Fill: {section.drums.fill}</p>
                            </div>
                          ))}
                        </div>
                      </article>
                    </div>

                    <div className={`notes-tab-panel ${activeTab === "piano" ? "active" : ""}`} role="tabpanel">
                      <article className="notes-card notes-card--expanded">
                        <header>
                          <FaKeyboard aria-hidden="true" />
                          <h3>Piano notes</h3>
                          <button
                            type="button"
                            className="notes-export"
                            onClick={() => downloadMidi("piano-notes.mid", buildChordMidi(notes.tempo, notes.sectionNotes, 60))}
                          >
                            Export MIDI
                          </button>
                        </header>
                        <div className="notes-stem-controls">
                          <button type="button" className="notes-play" onClick={() => playNotePreview("piano")}>
                            {activeNotePreview === "piano" ? "Stop notes" : "Play notes"}
                          </button>
                        </div>
                        {notes.sections.length > 0 && (
                          <div className="notes-section-list">
                            {notes.sections.map((section, index) => (
                              <div
                                key={`${section.label}-${section.start}`}
                                className={`notes-section-item ${activeSectionIndex === index ? "active" : ""}`}
                              >
                                <span>{section.label}</span>
                                <span>
                                  {formatTimestamp(section.start)} - {formatTimestamp(section.end)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="notes-detail-grid">
                          <div>
                            <h4>Progression</h4>
                            <p>{notes.piano.progression}</p>
                          </div>
                          <div>
                            <h4>Voicing</h4>
                            <p>{notes.piano.voicing}</p>
                          </div>
                          <div>
                            <h4>Rhythm</h4>
                            <p>{notes.piano.rhythm}</p>
                          </div>
                        </div>
                        <div className="notes-detail-columns">
                          <div>
                            <h4>Right hand</h4>
                            <ul>
                              {notes.piano.rightHand.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4>Left hand</h4>
                            <ul>
                              {notes.piano.leftHand.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4>Section variations</h4>
                            <ul>
                              {notes.piano.variations.map((line) => (
                                <li key={line}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="notes-section-cards">
                          {notes.sectionNotes.map((section, index) => (
                            <div
                              key={`${section.label}-${index}`}
                              className={`notes-section-card ${activeSectionIndex === index ? "active" : ""}`}
                            >
                              <h4>{section.label}</h4>
                              <p>Chords: {section.piano.progression}</p>
                              <p>Voicing: {section.piano.voicing}</p>
                            </div>
                          ))}
                        </div>
                      </article>
                    </div>

                    <div className={`notes-tab-panel ${activeTab === "violin" ? "active" : ""}`} role="tabpanel">
                      <article className="notes-card notes-card--expanded">
                        <header>
                          <FaMusic aria-hidden="true" />
                          <h3>Violin notes</h3>
                          <button
                            type="button"
                            className="notes-export"
                            onClick={() => downloadMidi("violin-notes.mid", buildChordMidi(notes.tempo, notes.sectionNotes, 72))}
                          >
                            Export MIDI
                          </button>
                        </header>
                        <div className="notes-stem-controls">
                          <button type="button" className="notes-play" onClick={() => playNotePreview("violin")}>
                            {activeNotePreview === "violin" ? "Stop notes" : "Play notes"}
                          </button>
                        </div>
                        {notes.sections.length > 0 && (
                          <div className="notes-section-list">
                            {notes.sections.map((section, index) => (
                              <div
                                key={`${section.label}-${section.start}`}
                                className={`notes-section-item ${activeSectionIndex === index ? "active" : ""}`}
                              >
                                <span>{section.label}</span>
                                <span>
                                  {formatTimestamp(section.start)} - {formatTimestamp(section.end)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="notes-detail-grid">
                          <div>
                            <h4>Main line</h4>
                            <p>{notes.violin.line}</p>
                          </div>
                          <div>
                            <h4>Articulation</h4>
                            <p>{notes.violin.articulation}</p>
                          </div>
                          <div>
                            <h4>Range</h4>
                            <p>{notes.violin.range}</p>
                          </div>
                        </div>
                        <div className="notes-detail-block">
                          <h4>Phrase map</h4>
                          <ul>
                            {notes.violin.phrases.map((line) => (
                              <li key={line}>{line}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="notes-section-cards">
                          {notes.sectionNotes.map((section, index) => (
                            <div
                              key={`${section.label}-${index}`}
                              className={`notes-section-card ${activeSectionIndex === index ? "active" : ""}`}
                            >
                              <h4>{section.label}</h4>
                              <p>Line: {section.violin.line}</p>
                              <p>Articulation: {section.violin.articulation}</p>
                            </div>
                          ))}
                        </div>
                      </article>
                    </div>

                    <div className={`notes-tab-panel ${activeTab === "guitar" ? "active" : ""}`} role="tabpanel">
                      <article className="notes-card notes-card--expanded">
                        <header>
                          <FaGuitar aria-hidden="true" />
                          <h3>Guitar notes</h3>
                          <button
                            type="button"
                            className="notes-export"
                            onClick={() => downloadMidi("guitar-notes.mid", buildChordMidi(notes.tempo, notes.sectionNotes, 52))}
                          >
                            Export MIDI
                          </button>
                        </header>
                        <div className="notes-stem-controls">
                          <button type="button" className="notes-play" onClick={() => playNotePreview("guitar")}>
                            {activeNotePreview === "guitar" ? "Stop notes" : "Play notes"}
                          </button>
                        </div>
                        <div className="notes-detail-grid">
                          <div>
                            <h4>Core chords</h4>
                            <p>{notes.sectionNotes[0]?.guitar.chords || "Use the song progression."}</p>
                          </div>
                          <div>
                            <h4>Strum pattern</h4>
                            <p>{notes.sectionNotes[0]?.guitar.strum || "Down • Down • Up • Up • Down • Up"}</p>
                          </div>
                          <div>
                            <h4>Texture</h4>
                            <p>{notes.sectionNotes[0]?.guitar.texture || "Let chords ring in the chorus."}</p>
                          </div>
                        </div>
                        <div className="notes-section-cards">
                          {notes.sectionNotes.map((section, index) => (
                            <div
                              key={`${section.label}-${index}`}
                              className={`notes-section-card ${activeSectionIndex === index ? "active" : ""}`}
                            >
                              <h4>{section.label}</h4>
                              <p>Chords: {section.guitar.chords}</p>
                              <p>Strum: {section.guitar.strum}</p>
                              <p>{section.guitar.texture}</p>
                            </div>
                          ))}
                        </div>
                      </article>
                    </div>

                    <div className={`notes-tab-panel ${activeTab === "pad" ? "active" : ""}`} role="tabpanel">
                      <div className="notes-pad-sheet">
                        <div className="notes-pad-header">
                          <div>
                            <h2>9-pad drum sheet</h2>
                            <p>
                              Time: {notes.padSheet.timeSignature} | Tempo: {notes.padSheet.tempoLabel} ({notes.tempo} BPM)
                            </p>
                          </div>
                          <div className="notes-pad-meta">Use pads 1-8 for the color-coded layout.</div>
                        </div>
                        <button
                          type="button"
                          className="notes-export notes-export--pad"
                          onClick={() => downloadMidi("pad-notes.mid", buildDrumMidi(notes.tempo, notes.sectionNotes))}
                        >
                          Export MIDI
                        </button>
                        <div className="notes-stem-controls">
                          <button type="button" className="notes-play" onClick={() => playNotePreview("pad")}>
                            {activeNotePreview === "pad" ? "Stop notes" : "Play notes"}
                          </button>
                        </div>

                        <div className="notes-pad-legend">
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-yellow" aria-hidden="true" />
                            <span>Pad 2: Hi-Hat</span>
                          </div>
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-red" aria-hidden="true" />
                            <span>Pad 4: Snare</span>
                          </div>
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-blue" aria-hidden="true" />
                            <span>Pad 7: Kick</span>
                          </div>
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-green" aria-hidden="true" />
                            <span>Pad 1: Crash</span>
                          </div>
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-purple" aria-hidden="true" />
                            <span>Pad 3: Ride</span>
                          </div>
                          <div className="notes-legend-item">
                            <span className="notes-swatch swatch-orange" aria-hidden="true" />
                            <span>Pad 5/6/8: Toms</span>
                          </div>
                        </div>

                        <div className="notes-pad-symbols">
                          <div className="notes-symbol-row">
                            <span className="notes-symbol notes-symbol-x" aria-hidden="true">x</span>
                            <span>Light tap / steady time</span>
                          </div>
                          <div className="notes-symbol-row">
                            <span className="notes-symbol notes-symbol-o" aria-hidden="true">o</span>
                            <span>Main hit</span>
                          </div>
                          <div className="notes-symbol-row">
                            <span className="notes-symbol notes-symbol-O" aria-hidden="true">O</span>
                            <span>Strong accent</span>
                          </div>
                          <div className="notes-symbol-row">
                            <span className="notes-symbol notes-symbol-X" aria-hidden="true">X</span>
                            <span>Crash accent</span>
                          </div>
                        </div>

                        <div className="notes-pad-note">Most of the song uses Hi-Hat, Snare, and Kick.</div>

                        <div className="notes-pad-sections">
                          {notes.padSheet.sections.map((section) => (
                            <div
                              key={section.title}
                              className={`notes-pad-section ${
                                typeof section.start === "number" &&
                                typeof section.end === "number" &&
                                currentTime >= section.start &&
                                currentTime < section.end
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <h3>{section.title}</h3>
                              <div className="notes-pad-grid">
                                <div className="notes-pad-row notes-pad-count">
                                  <span className="notes-pad-label">Count</span>
                                  {section.counts.map((count, index) => (
                                    <span key={`${section.title}-count-${index}`} className="notes-pad-cell">
                                      {count}
                                    </span>
                                  ))}
                                </div>
                                {section.rows.map((row) => (
                                  <div key={`${section.title}-${row.label}`} className="notes-pad-row">
                                    <span className="notes-pad-label">{row.label}</span>
                                    {row.hits.map((hit, index) => (
                                      <span key={`${section.title}-${row.label}-${index}`} className="notes-pad-cell">
                                        {hit || "\u00A0"}
                                      </span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                              {section.note && <p className="notes-pad-note">{section.note}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
              ) : (
                <p className="notes-player-empty">Generate notes to see the full score.</p>
              )}
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
